import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarClock,
  Download,
  LoaderCircle,
  Pencil,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  ConfirmDialog,
  FeedbackBanner,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import {
  findMendozaWorkflowCatalogOption,
  isMendozaWorkflowCatalogProperty,
  mendozaWorkflowCourtLevel,
  mendozaWorkflowCatalogOptions,
  normalizeCuij,
} from "@tribunalea/shared";
import { useApi } from "../App.jsx";
import {
  WorkflowComputedPercentageField,
} from "../components/WorkflowComputedPercentageField.jsx";
import {
  downloadFile,
  WorkflowRecordDownloadDialog,
  WorkflowRecordTextDialog,
} from "../components/WorkflowRecordExportDialogs.jsx";
import {
  WorkflowJudicialInterestField,
} from "../components/WorkflowJudicialInterestField.jsx";
import {
  WorkflowLegalContributionField,
} from "../components/WorkflowLegalContributionField.jsx";

function emptyValueFor(field) {
  if (field.type === "boolean") return false;
  if ([
    "judicial_interest",
    "computed_percentage",
    "computed_date",
    "legal_contribution",
  ].includes(field.type)) {
    return null;
  }
  return "";
}

function validDateOnly(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime())
    && parsed.toISOString().slice(0, 10) === value;
}

function computedDateValue(field, values) {
  const config = field.computedDate;
  const source = values[config?.inputFieldId];
  const sourceValue = config?.sourceProperty === "value"
    ? source
    : source?.[config?.sourceProperty];
  if (!validDateOnly(sourceValue)) return null;
  const offsetDays = Number(config.offsetDays);
  if (!Number.isInteger(offsetDays)) return null;
  const date = new Date(`${sourceValue}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function initialValue(field, storedValue) {
  if (storedValue === undefined || storedValue === null) {
    return emptyValueFor(field);
  }
  if (field.valueSource?.property === "organismo") {
    return mendozaWorkflowCourtLevel(storedValue);
  }
  if (field.type !== "select") return storedValue;
  return field.options?.find(({ id, label }) => (
    id === storedValue
    || label.toLocaleLowerCase("es-AR")
      === String(storedValue).toLocaleLowerCase("es-AR")
  ))?.id || "";
}

function initialValues(fields, storedValues = {}) {
  return Object.fromEntries(fields.map((field) => [
    field.id,
    initialValue(field, storedValues[field.id]),
  ]));
}

function pendingJudicialInterest(value) {
  return value?.status === "processing" && Boolean(value.calculationId);
}

function expedienteSourceValue(fields, values, property) {
  const field = fields.find((candidate) => (
    candidate.valueSource?.kind === "expediente"
    && candidate.valueSource.property === property
  ));
  return field ? values[field.id] : "";
}

function mendozaCatalogContext(fields, values) {
  return {
    fuero: expedienteSourceValue(fields, values, "fuero"),
    procedureType: expedienteSourceValue(fields, values, "procedure_type"),
  };
}

function usesMendozaCatalog(field, values) {
  if (!isMendozaWorkflowCatalogProperty(field.valueSource?.property)) {
    return false;
  }
  const cuij = normalizeCuij(values[field.valueSource.inputFieldId]);
  return !cuij || cuij.startsWith("13");
}

function WorkflowExpedienteCatalogControl({
  field,
  fields,
  values,
  onChange,
  onRefresh,
  resolving,
}) {
  const [manualMode, setManualMode] = useState(false);
  const property = field.valueSource.property;
  const currentValue = String(values[field.id] || "");
  const allowsManual = property !== "organismo";
  const context = mendozaCatalogContext(fields, values);
  const options = mendozaWorkflowCatalogOptions(property, context);
  const matched = findMendozaWorkflowCatalogOption(
    property,
    currentValue,
    context,
  );
  const visibleMatch = matched && options.some(({ id }) => id === matched.id)
    ? matched
    : null;
  const showManual = allowsManual
    && (manualMode || Boolean(currentValue && !visibleMatch));
  const selectedValue = showManual ? "__manual__" : visibleMatch?.value || "";
  const groups = options.reduce((result, option) => {
    const group = option.group || "Opciones";
    if (!result.has(group)) result.set(group, []);
    result.get(group).push(option);
    return result;
  }, new Map());
  const sourceReady = Boolean(String(
    values[field.valueSource.inputFieldId] || "",
  ).trim());
  return (
    <>
      <span>
        <Sparkles size={13} />
        {field.label}
        {field.required && <strong> *</strong>}
      </span>
      <div className="workflow-record-catalog-control">
        <select
          value={selectedValue}
          required={field.required && !showManual}
          aria-label={field.label}
          onChange={(event) => {
            if (event.target.value === "__manual__") {
              setManualMode(true);
              return;
            }
            setManualMode(false);
            onChange(event.target.value);
          }}
        >
          <option value="">
            {field.required ? "Seleccioná una opción" : "Sin seleccionar"}
          </option>
          {[...groups.entries()].map(([group, entries]) => (
            <optgroup label={group} key={group}>
              {entries.map((option) => (
                <option value={option.value} key={option.id}>
                  {option.label}{option.status === "historical" ? " · histórico" : ""}
                </option>
              ))}
            </optgroup>
          ))}
          {allowsManual && (
            <option value="__manual__">Otro valor o valor histórico…</option>
          )}
        </select>
        <button
          type="button"
          disabled={resolving || !sourceReady}
          onClick={onRefresh}
        >
          {resolving ? (
            <LoaderCircle className="spin" size={15} />
          ) : (
            <RefreshCw size={15} />
          )}
          {resolving ? "Actualizando…" : "Actualizar"}
        </button>
      </div>
      {showManual && (
        <input
          className="workflow-record-catalog-manual"
          type="text"
          value={currentValue}
          required={field.required}
          maxLength={5_000}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Escribí el valor no catalogado"
          autoFocus={manualMode && !currentValue}
        />
      )}
      <small>
        {showManual
          ? "El valor actual no coincide con las opciones compatibles. Se conserva como histórico hasta que elijas otro."
          : property === "organismo"
            ? "Indicá si el expediente está en Tribunales, Cámara o Suprema Corte. Actualizar vuelve a inferirlo desde el organismo publicado."
            : "Las opciones se filtran con los demás datos del expediente. Actualizar restaura el dato publicado."}
      </small>
    </>
  );
}

const readonlyNumber = new Intl.NumberFormat("es-AR", {
  maximumFractionDigits: 8,
});

const readonlyDate = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
});

function readOnlyValue(field, value) {
  if (field.type === "boolean") return value ? "Sí" : "No";
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "select") {
    return field.options?.find(({ id, label }) => (
      id === value
      || label.toLocaleLowerCase("es-AR")
        === String(value).toLocaleLowerCase("es-AR")
    ))?.label || String(value);
  }
  if (["date", "computed_date"].includes(field.type)) {
    const [year, month, day] = String(value).split("-").map(Number);
    return year && month && day
      ? readonlyDate.format(new Date(year, month - 1, day, 12))
      : String(value);
  }
  if (field.type === "number") return readonlyNumber.format(Number(value));
  return String(value);
}

export function WorkflowRecordFormPage({
  templateId,
  recordId = null,
  readOnly = false,
}) {
  const api = useApi();
  const { navigate } = useRouter();
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");
  const [smartError, setSmartError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [preparingFormat, setPreparingFormat] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [textPreview, setTextPreview] = useState(null);

  useEffect(() => {
    let active = true;
    const templateRequest = api.getWorkflowTemplate(templateId);
    const recordRequest = recordId
      ? api.getWorkflowRecord(templateId, recordId)
      : Promise.resolve(null);
    Promise.all([templateRequest, recordRequest])
      .then(([templateResult, recordResult]) => {
        if (!active) return;
        setTemplate(templateResult);
        setValues(initialValues(
          templateResult.fields,
          recordResult?.values,
        ));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [api, templateId, recordId]);

  const saveState = useMemo(() => {
    const fields = template?.fields || [];
    const pending = fields.some((field) => (
      field.type === "judicial_interest"
      && pendingJudicialInterest(values[field.id])
    ));
    const valid = fields.every((field) => {
      if (field.type === "judicial_interest") {
        return !field.required
          || values[field.id]?.status === "calculated"
          || pendingJudicialInterest(values[field.id]);
      }
      if (field.type === "computed_percentage") {
        const source = values[field.computedPercentage?.inputFieldId];
        return !field.required
          || source?.status === "calculated"
          || pendingJudicialInterest(source);
      }
      if (field.type === "computed_date") {
        const source = values[field.computedDate?.inputFieldId];
        return !field.required
          || Boolean(computedDateValue(field, values))
          || pendingJudicialInterest(source);
      }
      if (field.type === "legal_contribution") {
        const source = values[field.legalContribution?.inputFieldId];
        return !field.required
          || source?.status === "calculated"
          || pendingJudicialInterest(source);
      }
      if (!field.required || field.type === "boolean") return true;
      return values[field.id] !== ""
        && values[field.id] !== null
        && values[field.id] !== undefined;
    });
    return { pending, valid: Boolean(template) && valid };
  }, [template, values]);

  const recordLabel = useMemo(() => {
    const primaryField = template?.fields?.[0];
    if (!primaryField) return "Este registro";
    const label = readOnlyValue(primaryField, values[primaryField.id]);
    return label === "—" ? "Este registro" : label;
  }, [template, values]);

  const updateValue = (fieldId, value) => {
    setSmartError("");
    setValues((current) => {
      const next = { ...current, [fieldId]: value };
      const editedField = template?.fields.find(({ id }) => id === fieldId);
      template?.fields.forEach((field) => {
        if (
          field.valueSource?.inputFieldId === fieldId
          || field.judicialInterest?.inputFieldId === fieldId
          || field.computedPercentage?.inputFieldId === fieldId
          || field.computedDate?.inputFieldId === fieldId
          || field.legalContribution?.inputFieldId === fieldId
          || field.legalContribution?.cuijFieldId === fieldId
        ) {
          next[field.id] = [
            "judicial_interest",
            "computed_percentage",
            "computed_date",
            "legal_contribution",
          ]
            .includes(field.type) ? null : "";
        }
        if (
          editedField?.valueSource
          && field.legalContribution
          && next[field.id]
        ) {
          next[field.id] = {
            ...next[field.id],
            classificationStale: true,
          };
        }
      });
      return next;
    });
  };

  const resolveSmartValues = async (
    inputFieldId = null,
    refreshSources = true,
    refreshFieldIds = null,
  ) => {
    const smartFields = template?.fields.filter((field) => (
      field.valueSource
      && (!inputFieldId || field.valueSource.inputFieldId === inputFieldId)
    )) || [];
    const hasLegalContribution = template?.fields.some((field) => (
      field.legalContribution
      && (!inputFieldId
        || field.legalContribution.cuijFieldId === inputFieldId
        || field.legalContribution.inputFieldId === inputFieldId)
    ));
    if ((!smartFields.length && !hasLegalContribution) || resolving) return;
    if (smartFields.some((field) => (
      !String(values[field.valueSource.inputFieldId] || "").trim()
    ))) return;
    setResolving(true);
    setSmartError("");
    try {
      const result = await api.resolveWorkflowRecordValues(
        templateId,
        {
          values,
          refreshSources,
          ...(refreshFieldIds ? { refreshFieldIds } : {}),
        },
      );
      setValues((current) => ({ ...current, ...result.values }));
    } catch (requestError) {
      setSmartError(requestError.message);
    } finally {
      setResolving(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!saveState.valid || saving) return;
    setSaving(true);
    setError("");
    try {
      const input = { values };
      if (recordId) {
        await api.updateWorkflowRecord(templateId, recordId, input);
      } else {
        await api.createWorkflowRecord(templateId, input);
      }
      navigate(`/flujos/${templateId}`, { replace: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadRecordWord = async () => {
    if (!recordId || preparingFormat) return;
    setPreparingFormat("word");
    setDownloadError("");
    try {
      downloadFile(await api.exportWorkflowRecordWord(templateId, recordId));
      setDownloadOpen(false);
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const downloadRecordPdf = async () => {
    if (!recordId || preparingFormat) return;
    setPreparingFormat("pdf");
    setDownloadError("");
    try {
      downloadFile(await api.exportWorkflowRecordPdf(templateId, recordId));
      setDownloadOpen(false);
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const viewRecordText = async () => {
    if (!recordId || preparingFormat) return;
    setPreparingFormat("text");
    setDownloadError("");
    try {
      const result = await api.getWorkflowRecordText(templateId, recordId);
      setDownloadOpen(false);
      setTextPreview({ recordLabel, text: result.text });
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const deleteRecord = async () => {
    if (!recordId || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await api.deleteWorkflowRecord(templateId, recordId);
      navigate(`/flujos/${templateId}`, { replace: true });
    } catch (requestError) {
      setError(requestError.message);
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page workflow-record-form-page">
      <PageHeader
        eyebrow="Praxis · Flujos"
        title={readOnly
          ? "Ver registro"
          : recordId
            ? "Editar registro"
            : "Nuevo registro"}
        description={template
          ? readOnly
            ? `Consulta de los datos guardados en ${template.name}.`
            : `Completá los datos de ${template.name}.`
          : "Completá los datos definidos por la plantilla."}
        actions={(
          <div className="workflow-record-header-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => navigate(`/flujos/${templateId}`)}
            >
              <ArrowLeft size={16} />
              Volver
            </button>
            {readOnly && recordId && (
              <>
                <button
                  className="button secondary"
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setDownloadError("");
                    setDownloadOpen(true);
                  }}
                >
                  <Download size={16} />
                  Descargar
                </button>
                <button
                  className="button danger"
                  type="button"
                  disabled={loading}
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
                <button
                  className="button primary"
                  type="button"
                  disabled={loading}
                  onClick={() => navigate(
                    `/flujos/${templateId}/registros/${recordId}`,
                  )}
                >
                  <Pencil size={16} />
                  Editar
                </button>
              </>
            )}
          </div>
        )}
      />

      {error && <FeedbackBanner tone="error">{error}</FeedbackBanner>}
      {smartError && (
        <FeedbackBanner tone="error">{smartError}</FeedbackBanner>
      )}

      {loading ? (
        <div className="workflow-templates-loading">
          <LoaderCircle className="spin" size={23} />
          <span>Cargando formulario…</span>
        </div>
      ) : template && (
        <form
          className={`workflow-record-form${readOnly ? " is-readonly" : ""}`}
          onSubmit={save}
        >
          <section className="workflow-template-settings-card">
            <div className="workflow-template-section-heading">
              <div>
                <span className="eyebrow">{template.name}</span>
                <h2>Datos del registro</h2>
                <p>Los campos marcados con * son obligatorios.</p>
              </div>
            </div>

            <div className="workflow-record-fields">
              {template.fields.map((field) => {
                if (field.type === "judicial_interest") {
                  return (
                    <WorkflowJudicialInterestField
                      api={api}
                      templateId={templateId}
                      field={field}
                      cuij={values[field.judicialInterest?.inputFieldId] || ""}
                      value={values[field.id]}
                      onChange={(value) => updateValue(field.id, value)}
                      readOnly={readOnly}
                      key={`${field.id}:${values[field.judicialInterest?.inputFieldId] || ""}`}
                    />
                  );
                }
                if (field.type === "computed_percentage") {
                  return (
                    <WorkflowComputedPercentageField
                      field={field}
                      sourceValue={values[field.computedPercentage?.inputFieldId]}
                      key={field.id}
                    />
                  );
                }
                if (field.type === "legal_contribution") {
                  return (
                    <WorkflowLegalContributionField
                      field={field}
                      sourceValue={values[
                        field.legalContribution?.inputFieldId
                      ]}
                      value={values[field.id]}
                      onChange={(value) => updateValue(field.id, value)}
                      onResolve={() => resolveSmartValues(null, false)}
                      resolving={resolving}
                      readOnly={readOnly}
                      key={field.id}
                    />
                  );
                }
                if (readOnly) {
                  const value = field.type === "computed_date"
                    ? computedDateValue(field, values)
                    : values[field.id];
                  return (
                    <div
                      className="workflow-record-field workflow-record-readonly-field"
                      key={field.id}
                    >
                      <span className={field.valueSource || field.computedDate
                        ? "workflow-record-automated-label"
                        : undefined}
                      >
                        {field.valueSource && <Sparkles size={13} />}
                        {field.computedDate && <CalendarClock size={13} />}
                        {field.label}
                        {field.required && <strong> *</strong>}
                      </span>
                      <div>{readOnlyValue(field, value)}</div>
                    </div>
                  );
                }
                if (field.type === "computed_date") {
                  const value = computedDateValue(field, values);
                  const sourceField = template.fields.find(
                    ({ id }) => id === field.computedDate?.inputFieldId,
                  );
                  return (
                    <label
                      className="workflow-record-field workflow-record-smart-field workflow-record-computed-date-field"
                      key={field.id}
                    >
                      <span>
                        <CalendarClock size={13} />
                        {field.label}
                        {field.required && <strong> *</strong>}
                      </span>
                      <input
                        type="date"
                        value={value || ""}
                        readOnly
                        aria-label={`${field.label}${field.required ? " *" : ""}`}
                        placeholder="Se calcula automáticamente"
                      />
                      <small>
                        {field.computedDate.offsetDays} días después de {sourceField?.label || "la fecha de referencia"}.
                      </small>
                    </label>
                  );
                }
                return (
                <label
                  className={field.valueSource
                    ? "workflow-record-field workflow-record-smart-field"
                    : field.type === "boolean"
                      ? "workflow-record-field workflow-record-boolean-field"
                      : "workflow-record-field"}
                  key={field.id}
                >
                  {field.valueSource ? (
                    usesMendozaCatalog(field, values) ? (
                      <WorkflowExpedienteCatalogControl
                        key={`${field.id}:${values[field.id] || ""}`}
                        field={field}
                        fields={template.fields}
                        values={values}
                        onChange={(value) => updateValue(field.id, value)}
                        onRefresh={() => resolveSmartValues(
                          field.valueSource.inputFieldId,
                          true,
                          [field.id],
                        )}
                        resolving={resolving}
                      />
                    ) : (
                      <>
                        <span>
                          <Sparkles size={13} />
                          {field.label}
                          {field.required && <strong> *</strong>}
                        </span>
                        <div>
                          <input
                            type="text"
                            value={values[field.id] ?? ""}
                            required={field.required}
                            maxLength={5_000}
                            onChange={(event) => updateValue(
                              field.id,
                              event.target.value,
                            )}
                            placeholder="Dato sugerido desde el expediente"
                          />
                          <button
                            type="button"
                            disabled={resolving || !String(
                              values[field.valueSource.inputFieldId] || "",
                            ).trim()}
                            onClick={() => resolveSmartValues(
                              field.valueSource.inputFieldId,
                              true,
                              [field.id],
                            )}
                          >
                            {resolving ? (
                              <LoaderCircle className="spin" size={15} />
                            ) : (
                              <RefreshCw size={15} />
                            )}
                            {resolving ? "Actualizando…" : "Actualizar"}
                          </button>
                        </div>
                        <small>
                          Se completa desde el expediente, pero podés corregirlo
                          manualmente. Actualizar restaura este dato publicado.
                        </small>
                      </>
                    )
                  ) : field.type === "boolean" ? (
                    <>
                      <span>
                        {field.label}
                        {field.required && <strong> *</strong>}
                      </span>
                      <span className="workflow-record-boolean-control">
                        <input
                          type="checkbox"
                          checked={Boolean(values[field.id])}
                          aria-label={field.label}
                          onChange={(event) => updateValue(
                            field.id,
                            event.target.checked,
                          )}
                        />
                        <span>{values[field.id] ? "Sí" : "No"}</span>
                      </span>
                    </>
                  ) : field.type === "select" ? (
                    <>
                      <span>
                        {field.label}
                        {field.required && <strong> *</strong>}
                      </span>
                      <select
                        value={values[field.id] ?? ""}
                        required={field.required}
                        onChange={(event) => updateValue(
                          field.id,
                          event.target.value,
                        )}
                      >
                        <option value="">
                          {field.required ? "Seleccioná una opción" : "Sin seleccionar"}
                        </option>
                        {field.options?.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <span>
                        {field.label}
                        {field.required && <strong> *</strong>}
                      </span>
                      <input
                        type={field.type === "text" ? "text" : field.type}
                        step={field.type === "number" ? "any" : undefined}
                        value={values[field.id] ?? ""}
                        required={field.required}
                        maxLength={field.type === "text" ? 5_000 : undefined}
                        onChange={(event) => updateValue(
                          field.id,
                          event.target.value,
                        )}
                        onBlur={() => resolveSmartValues(field.id)}
                      />
                    </>
                  )}
                </label>
                );
              })}
            </div>
          </section>

          {!readOnly && <div className="workflow-template-form-actions">
            <button
              className="button primary"
              type="submit"
              disabled={!saveState.valid || saving}
            >
              {saving ? (
                <LoaderCircle className="spin" size={17} />
              ) : (
                <Save size={17} />
              )}
              {saving
                ? "Guardando…"
                : saveState.pending
                  ? "Guardar y continuar después"
                  : "Guardar registro"}
            </button>
          </div>}
        </form>
      )}

      {downloadOpen && (
        <WorkflowRecordDownloadDialog
          recordLabel={recordLabel}
          busyFormat={preparingFormat}
          error={downloadError}
          onDownloadWord={downloadRecordWord}
          onDownloadPdf={downloadRecordPdf}
          onViewText={viewRecordText}
          onClose={() => {
            if (!preparingFormat) setDownloadOpen(false);
          }}
        />
      )}

      {textPreview && (
        <WorkflowRecordTextDialog
          recordLabel={textPreview.recordLabel}
          text={textPreview.text}
          onClose={() => setTextPreview(null)}
        />
      )}

      {deleteOpen && (
        <ConfirmDialog
          title="Eliminar registro"
          description={`“${recordLabel}” dejará de verse en este flujo. Los datos quedarán archivados para conservar la trazabilidad.`}
          confirmLabel={deleting ? "Eliminando…" : "Eliminar registro"}
          busy={deleting}
          onConfirm={deleteRecord}
          onClose={() => {
            if (!deleting) setDeleteOpen(false);
          }}
        />
      )}
    </div>
  );
}
